import { Card } from "@firecms/ui";

import { type TeamStats } from "../types/stats";
import {
  statsLocaleBadge,
  statsMuted,
  statsParticipantName,
  statsProgressTrack,
  statsSectionTitle,
  statsSubtle,
  statsTeamCardInactive,
} from "./statsViewTheme";

type EventCardProps = {
  teamStats: TeamStats;
};

function initials(name: string) {
  return name.substring(0, 2).toUpperCase();
}

function clampPct(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

function localeColor(locale: string): string {
  if (locale === "en") return "#3b82f6";
  if (locale === "fr") return "#a855f7";
  if (locale === "pt") return "#22c55e";
  return "#ef4444";
}

export const TeamCard = ({ teamStats }: EventCardProps) => {
  const teamNumberLabel = String(teamStats.team.values.teamNumber ?? "");
  const maxParts = teamStats.team.values.maxMembers;
  const parts = teamStats.team.values.membersCount;
  const isActive = teamStats.participants.length > 0;
  const locale = teamStats.team.values.locale;

  const partProgress = clampPct((parts / maxParts) * 100);
  const localeLabel = typeof locale === "string" ? locale : "any";
  const avatarColor = localeColor(localeLabel);

  return (
    <Card
      className={
        isActive
          ? "border-surface-200 dark:border-surface-700 dark:bg-surface-800"
          : statsTeamCardInactive
      }
    >
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="min-w-0">
          <div className={`truncate ${statsSectionTitle}`}>
            Team {teamNumberLabel}
          </div>
          <div className={`mt-1 text-sm ${statsMuted}`}>
            {teamStats.participants.length} members
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <span className={statsLocaleBadge}>{localeLabel}</span>
          <div
            className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold text-white shadow-sm"
            style={{ backgroundColor: avatarColor }}
            aria-label="Team number"
            title={`Team number ${teamNumberLabel}`}
          >
            {teamNumberLabel}
          </div>
        </div>
      </div>

      <div className="px-4 pb-4">
        <div className="flex items-center gap-3">
          <div className={statsProgressTrack}>
            <div
              className="h-full rounded-full bg-sky-500 dark:bg-sky-400"
              style={{ width: `${partProgress}%` }}
            />
          </div>
          <div className={`w-12 text-right text-xs ${statsMuted}`}>
            {Math.round(partProgress)}%
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {teamStats.participants.length === 0 ? (
            <p className={`text-xs ${statsSubtle}`}>No members yet.</p>
          ) : (
            teamStats.participants.map((participant) => {
              const label =
                participant.id ??
                (participant.values.participantNumber != null
                  ? `Cupo ${participant.values.participantNumber}`
                  : "Participant");
              return (
              <div key={participant.id} className="flex items-center gap-3">
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-500 text-xs font-semibold text-white dark:bg-surface-600"
                  title={label}
                >
                  {initials(label)}
                </div>
                <div className="min-w-0">
                  <div className={statsParticipantName}>{label}</div>
                  <div className={`truncate text-xs ${statsSubtle}`}>
                    {participant.values.participantNumber != null
                      ? `Cupo ${participant.values.participantNumber}`
                      : participant.id}
                  </div>
                </div>
              </div>
            );
            })
          )}
        </div>
      </div>
    </Card>
  );
};
